import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import BackButton from '../../components/common/BackButton';

const ManageServices = () => {
  const { t } = useTranslation();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    nameEn: '',
    icon: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchServices();

  }, []);

  const fetchServices = async () => {
    try {
      const res = await axios.get('https://hasanhaji.com/api/services');
      setServices(res.data.data.services);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching services:', err);
      setError(t('admin.errorLoadingServices'));
      setLoading(false);
    }
  };

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const handleChange = (e) => {
    if (e.target.name === 'image') {
      const file = e.target.files[0];
      setImageFile(file);
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setImagePreview(null);
      }
    } else {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);


    const data = new FormData();
    data.append('name', formData.name);
    data.append('nameEn', formData.nameEn);

    if (imageFile) {
      data.append('image', imageFile);
    } else if (!editingService) {


      if (formData.icon) data.append('icon', formData.icon);
    } else {

      data.append('icon', formData.icon);
    }

    try {
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      if (editingService) {

        await axios.patch(`https://hasanhaji.com/api/services/${editingService.id}`, data, config);
      } else {

        await axios.post('https://hasanhaji.com/api/services', data, config);
      }


      fetchServices();


      resetForm();
      setIsSubmitting(false);
    } catch (err) {
      console.error('Error saving service:', err);
      setError(t('admin.errorSavingService'));
      setIsSubmitting(false);
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      nameEn: service.nameEn,
      icon: service.icon,
    });
    setImageFile(null);

    if (service.icon && (service.icon.startsWith('/images/') || service.icon.startsWith('http'))) {
      setImagePreview(service.icon);
    } else {
      setImagePreview(null);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('admin.confirmDeleteService'))) {
      try {
        await axios.delete(`https://hasanhaji.com/api/services/${id}`);
        fetchServices();
      } catch (err) {
        console.error('Error deleting service:', err);
        setError(t('admin.errorDeletingService'));
      }
    }
  };

  const resetForm = () => {
    setEditingService(null);
    setFormData({
      name: '',
      nameEn: '',
      icon: '',
    });
    setImageFile(null);
    setImagePreview(null);
    const imageInput = document.getElementById('image');
    if (imageInput) imageInput.value = '';
  };

  return (
    <div className="container py-5">
      <div className="d-flex align-items-center gap-3 mb-4">
        <BackButton to="/admin" />
        <h2 className="mb-0">{t('admin.manageServicesTitle')}</h2>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="row">
        <div className="col-lg-4 mb-4">
          <div className="card shadow-sm">
            <div className="card-header bg-primary text-white">
              <h5 className="card-title mb-0">
                {editingService ? t('admin.editService') : t('admin.addServiceHeader')}
              </h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="name" className="form-label">
                    {t('admin.serviceNameAr')}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder={t('admin.serviceNameArPlaceholder')}
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="nameEn" className="form-label">
                    {t('admin.serviceNameEn')}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="nameEn"
                    name="nameEn"
                    value={formData.nameEn}
                    onChange={handleChange}
                    required
                    placeholder={t('admin.serviceNameEnPlaceholder')}
                    dir="ltr"
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="image" className="form-label">
                    {t('admin.serviceIcon')}
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    id="image"
                    name="image"
                    accept="image/*"
                    onChange={handleChange}
                  />
                  <small className="form-text text-muted">
                    {t('admin.imageUploadHint')}
                  </small>

                  { }
                  <div className="mt-3 text-center p-3 bg-light rounded">
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{ maxWidth: '100px', maxHeight: '100px', objectFit: 'contain' }}
                      />
                    ) : formData.icon && !formData.icon.startsWith('/') ? (
                      <i className={`fas fa-${formData.icon} fa-3x text-primary`}></i>
                    ) : (
                      <span className="text-muted">{t('admin.noPreview')}</span>
                    )}
                  </div>
                </div>

                <div className="d-grid gap-2">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {t('admin.saving')}
                      </>
                    ) : (
                      t('admin.saveService')
                    )}
                  </button>

                  {editingService && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={resetForm}
                    >
                      {t('admin.cancel')}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-lg-8">
          <div className="card shadow-sm">
            <div className="card-header bg-light">
              <h5 className="card-title mb-0">{t('admin.servicesList')}</h5>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : services.length === 0 ? (
                <div className="alert alert-info text-center" role="alert">
                  {t('admin.noServicesFound')}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th scope="col">{t('admin.serviceColumn')}</th>
                        <th scope="col">{t('admin.englishNameColumn')}</th>
                        <th scope="col" className="text-end">{t('admin.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {services.map((service) => (
                        <tr key={service.id}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="bg-light rounded-circle p-2 me-3 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', overflow: 'hidden' }}>
                                {service.icon && (service.icon.startsWith('/images/') || service.icon.startsWith('http')) ? (
                                  <img src={service.icon} alt={service.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                  <i className={`fas fa-${service.icon} text-primary`}></i>
                                )}
                              </div>
                              <span className="fw-bold">{service.name}</span>
                            </div>
                          </td>
                          <td>{service.nameEn}</td>
                          <td className="text-end">
                            <button
                              className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => handleEdit(service)}
                              title={t('admin.edit')}
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(service.id)}
                              title={t('admin.delete')}
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageServices;
